import React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';
import { FormControl } from '@common/components/form-control';
import { getTestAttributes } from '@common/utils/tests';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { MODULE_NAME } from '@modules/pedigrees/constants/shared';
import { Checkbox } from '@common/components/checkbox';
import { HttpErrorDto } from '@common/dto/http';

import './pedigree-renaming.styles.scss';

export type PedigreeRenamingProps = {
  onClose: () => void;
  onSubmit: (data: { name: string; renameIndividuals: boolean }) => Promise<void>;
  showErrorMessage: (msg: string) => void;
  defaultValue: string;
};

export const PedigreeRenaming: React.FC<PedigreeRenamingProps> = (props) => {
  const localModuleName = `${MODULE_NAME}-renaming-pedigree-modal`;
  const { onClose, onSubmit, showErrorMessage, defaultValue } = props;
  const [name, setName] = React.useState('');
  const [renameIndividuals, setRenameIndividuals] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);

    try {
      await onSubmit({ name: name.trim(), renameIndividuals });
    } catch (err) {
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to rename pedigree'));
      console.error(err);
    }
  }, [name, onSubmit, showErrorMessage, renameIndividuals]);

  return (
    <Modal 
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="PedigreesPedigreeRenaming"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle className="PedigreesPedigreeRenaming__title" {...getTestAttributes(localModuleName, 'header', 'title')} title={defaultValue}>
          {I18n.t('Rename Pedigree')} - {defaultValue}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <Flex justify="space-between" alignItems="center" className="PedigreesPedigreeRenaming__name-input-wrapper">
          <FormControl 
            placeholder={I18n.t('Type new pedigree name')}
            className="PedigreesPedigreeRenaming__name-input"
            {...getTestAttributes({ id: 'tx-rename-ped-name' })}
            onChange={handleChange}
          />
          <Button 
            className="PedigreesPedigreeRenaming__submit-btn"
            disabled={!name.trim().length || submitting || defaultValue === name.trim()}
            {...getTestAttributes({ id: 'btn-rename-ped-save' })}
            onClick={handleSubmit}
          >
            {submitting ? I18n.t('Loading') : I18n.t('Rename')}
          </Button>
        </Flex>
        <Checkbox 
          checked={renameIndividuals}
          {...getTestAttributes({ id: 'cbx-rename-ped-individual-input' })}
          label={I18n.t('Rename Individual Names')}
          onChange={setRenameIndividuals}
        />
      </ModalBody>
    </Modal>
  );
};